/**
 * @module Loader
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
import { Injectable } from '@angular/core';
import { Route, Router } from '@angular/router';
import { forkJoin as observableForkJoin, Observable } from 'rxjs';
import { DynamicRoute } from '@core/loader/interfaces/dynamic-route.interface';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { DynamicRouteComponent } from '@core/aem/components/dynamic-route/dynamic-route.component';
import { API_MAP } from '@support/api/api-map';
import { ContentMetaData, ContentManifest } from '@core/aem/models/deprecated-content.interface';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
/**
 * Service used for retrieving dynamic AEM routes and registering them to the global router config.
 * @note AEM Routes are not lazy loaded
 * @access protected - used by Core on load
 * @deprecated
 */
@Injectable()
export class DynamicRouteLoaderService {
  protected contentManifest: ContentManifest;
  protected routes: DynamicRoute[];

  constructor(
    private http: HttpClient,
    private contentProvider: ContentProviderService,
    private router: Router
  ) {}

  public loadDynamicRoutes(): void {
    this.loadData();
  }

  private loadData(): void {
    this.getRouteData().subscribe(
      (routeData: { manifest: ContentManifest; routes: DynamicRoute[] }) => {
        const contentStream: Observable<ContentMetaData>[] = [];
        // The order-management is important so we serialize streams one at a time
        const routeDataLength = routeData.routes.length;
        for (let i: number = 0; i < routeDataLength; i++) {
          contentStream.push(this.contentProvider.getContent(routeData.routes[i].id));
        }

        // @TODO this assumes all content meta data is resolved
        // Using the same index in the previous for loop we will iterate both arrays routes and content data map
        // We assume that the index of routes corresponds with the index of the content data map
        observableForkJoin(contentStream).subscribe((contentMap: ContentMetaData[]) => {
          let newRoutes: Route[] = [];
          const mapLen = contentMap.length;
          for (let i: number = 0; i < mapLen; i++) {
            if (contentMap[i].id === routeData.routes[i].id) {
              newRoutes.push({
                path: routeData.routes[i].path,
                component: DynamicRouteComponent,
                pathMatch: 'full',
                data: contentMap[i],
              });
            } else {
              throw new Error("Map mismatch, route data and content map id's are not the same");
            }
          }
          newRoutes = this.router.config.concat(newRoutes);
          console.log('New Routes', newRoutes);
          this.router.resetConfig(newRoutes);
          console.log('Set Routes', this.router.config);
        });
      }
    );
  }

  public getContent(id: string): Observable<ContentMetaData> {
    return this.contentProvider.getContent(id);
  }

  public getRouteData(): Observable<{ manifest: ContentManifest; routes: DynamicRoute[] }> {
    return observableForkJoin(this.contentProvider.retrieveManifest(), this.getRoutes()).pipe(
      map((res: [ContentManifest, DynamicRoute[]]) => {
        // Save values
        this.contentManifest = res[0];
        this.routes = res[1];
        return { manifest: res[0], routes: res[1] };
      })
    );
  }

  private getRoutes(): Observable<DynamicRoute[]> {
    return this.http.get<DynamicRoute[]>(API_MAP.AEM_ROUTES);
  }
}
